import { supabase } from '@/lib/supabaseClient';

export interface QueueEmailParams {
  tenantId: string;
  toEmail: string;
  subject: string;
  body: string;
  templateType?: string;
  entityType?: string;
  entityId?: string;
  metadata?: Record<string, unknown>;
  priority?: number;
  scheduledFor?: string;
}

/**
 * Inserts an email into the email_queue table.
 * Does NOT send anything — a future backend worker will process pending emails.
 * Fire-and-forget — does not throw.
 */
export async function queueEmail({
  tenantId,
  toEmail,
  subject,
  body,
  templateType = 'generic',
  entityType,
  entityId,
  metadata,
  priority,
  scheduledFor,
}: QueueEmailParams): Promise<void> {
  if (!toEmail || !toEmail.includes('@')) return;

  try {
    await supabase.from('email_queue').insert({
      tenant_id:     tenantId,
      to_email:      toEmail,
      subject,
      body,
      template_type: templateType,
      entity_type:   entityType ?? null,
      entity_id:     entityId   ?? null,
      metadata:      metadata   ?? null,
      priority:      priority   ?? 0,
      scheduled_for: scheduledFor ?? null,
      status:        'pending',
    });
  } catch {
    // Silently ignore — email queue is non-critical
  }
}

/* ─── Email body templates ─────────────────────────────────── */

export function contractCreatedBody(params: {
  clientName: string;
  contractNumber: string;
  contractTitle: string;
  eventDate: string;
  totalAmount: number;
}): string {
  return `
Hello ${params.clientName},

Your contract has been created. Here are the details:

  Contract Number : ${params.contractNumber}
  Title           : ${params.contractTitle}
  Event Date      : ${params.eventDate || 'TBD'}
  Total Amount    : $${params.totalAmount.toLocaleString()}

Please review and reach out if you have any questions.

Thank you for choosing us!
`.trim();
}

export function paymentReceivedBody(params: {
  clientName: string;
  amount: number;
  method: string;
  reference: string;
  paymentDate: string;
}): string {
  return `
Hello ${params.clientName},

We have recorded your payment. Details:

  Amount       : $${params.amount.toLocaleString()}
  Method       : ${params.method}
  Reference    : ${params.reference || 'N/A'}
  Payment Date : ${params.paymentDate}

Thank you — your payment has been successfully logged.
`.trim();
}

export function invitationCreatedBody(params: {
  clientName: string;
  invitationTitle: string;
  eventType: string;
  eventDate: string;
  publicSlug: string;
}): string {
  return `
Hello ${params.clientName},

Your digital invitation has been prepared. Here are the details:

  Title      : ${params.invitationTitle}
  Event Type : ${params.eventType}
  Event Date : ${params.eventDate || 'TBD'}
  Public URL : /i/${params.publicSlug}

We will notify you once the invitation is published and ready to share.

Thank you!
`.trim();
}

export function deliveryReadyBody(params: {
  clientName: string;
  jobTitle: string;
  portalUrl: string;
  deliveryCount?: number;
}): string {
  return `
Hello ${params.clientName},

Great news — your files are ready! 🎉

  Job    : ${params.jobTitle}
${params.deliveryCount ? `  Files  : ${params.deliveryCount} items uploaded` : ''}

You can view and download your delivery here:
  ${params.portalUrl}

If you have any questions, feel free to reach out directly through your client portal.

Thank you for trusting us!
`.trim();
}

export function appointmentReminderBody(params: {
  clientName: string;
  appointmentTitle: string;
  appointmentDate: string;
  appointmentTime: string;
  location?: string;
  studioName: string;
}): string {
  return `
Hello ${params.clientName},

This is a friendly reminder about your upcoming appointment.

  Appointment : ${params.appointmentTitle}
  Date      : ${params.appointmentDate}
  Time      : ${params.appointmentTime}
${params.location ? `  Location  : ${params.location}` : ''}

We're looking forward to seeing you!

— ${params.studioName}
`.trim();
}

export function invitationPublishedBody(params: {
  clientName: string;
  invitationTitle: string;
  eventType: string;
  eventDate?: string;
  publicUrl: string;
}): string {
  return `
Hello ${params.clientName},

Your digital invitation is now live and ready to share! 🎉

  Title      : ${params.invitationTitle}
  Event Type : ${params.eventType}
${params.eventDate ? `  Event Date : ${params.eventDate}` : ''}
  Public URL : ${params.publicUrl}

Share it with your guests via WhatsApp, email, or any messaging app.

Thank you for choosing us!
`.trim();
}